import React, { useState } from 'react';
import { Package, Mail, User, MapPin, Phone, CheckCircle } from 'lucide-react';
import { casilleroInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CasilleroSection = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    cedula: '',
    direccion: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [casilleroNumber, setCasilleroNumber] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/casillero`, formData);
      
      setCasilleroNumber(response.data.casillero_number);
      setIsSubmitted(true);
      
      // Reset form
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        cedula: '',
        direccion: ''
      });
      
    } catch (err) {
      setError('Hubo un error al procesar tu solicitud. Por favor intenta de nuevo.');
      console.error('Error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="casillero" className="py-20 px-6" style={{ background: 'var(--bg-section)' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--sendhapy-teal)' }}>
            Obtén tu casillero en Miami
          </h2>
          <p className="text-xl text-gray-600">
            Tu dirección en Miami lista para comprar. Abre tu casillero gratis hoy mismo
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Benefits & Example Address */}
          <div>
            <div className="sendhapy-card mb-6">
              <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--sendhapy-teal)' }}>
                Beneficios de tu casillero
              </h3>
              <ul className="space-y-3">
                {casilleroInfo.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle 
                      size={20} 
                      className="flex-shrink-0 mt-1" 
                      style={{ color: 'var(--sendhapy-yellow)' }} 
                    />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Image */}
            <div className="mt-6 rounded-xl overflow-hidden">
              <img 
                src="https://images.pexels.com/photos/4440788/pexels-photo-4440788.jpeg"
                alt="Paquete internacional listo para envío"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>

          {/* Right: Form */}
          <div>
            <div className="sendhapy-card">
              <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--sendhapy-teal)' }}>
                Solicita tu casillero gratis
              </h3>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={40} className="text-green-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-green-600 mb-2">¡Solicitud Enviada!</h4>
                  <p className="text-gray-600 mb-2">
                    Tu número de casillero: <span className="font-bold">{casilleroNumber}</span>
                  </p>
                  <p className="text-gray-600 mb-6">
                    Hemos enviado un email a tu correo con todos los detalles de tu dirección en Miami.
                  </p>
                  <p className="text-sm text-gray-500">
                    ¿No recibiste el email? Revisa tu carpeta de spam o contáctanos a <a href="mailto:hola@sendhapy.com" className="text-teal-600 underline">hola@sendhapy.com</a>
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User size={16} className="inline mr-1" />
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-yellow-400 transition-colors"
                      placeholder="Juan Pérez García"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail size={16} className="inline mr-1" />
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-yellow-400 transition-colors"
                      placeholder="tu@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone size={16} className="inline mr-1" />
                      Teléfono / WhatsApp *
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-yellow-400 transition-colors"
                      placeholder="+506 8888-8888"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Package size={16} className="inline mr-1" />
                      Cédula *
                    </label>
                    <input
                      type="text"
                      name="cedula"
                      value={formData.cedula}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-yellow-400 transition-colors"
                      placeholder="1-2345-6789"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin size={16} className="inline mr-1" />
                      Dirección en Costa Rica *
                    </label>
                    <textarea
                      name="direccion"
                      value={formData.direccion}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-yellow-400 transition-colors resize-none"
                      placeholder="San José, Escazú, 100m sur de..."
                    />
                  </div>

                  <label className="flex items-start gap-2 text-sm text-gray-600">
                    <input type="checkbox" name="acepta_whatsapp" data-testid="casillero-whatsapp-checkbox" checked={!!formData.acepta_whatsapp}
                      onChange={(e) => setFormData({ ...formData, acepta_whatsapp: e.target.checked })} disabled={isSubmitting} className="mt-1 accent-teal-700" />
                    Acepto recibir mensajes de SendHapy por WhatsApp sobre mi casillero y mis paquetes.
                  </label>

                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-sendhapy-primary w-full"
                  >
                    {isSubmitting ? 'Procesando...' : 'Crear Casillero Gratis'}
                  </button>

                  <div className="text-center pt-4">
                    <p className="text-sm text-gray-500 mb-3">O contáctanos directamente:</p>
                    <a 
                      href="mailto:hola@sendhapy.com"
                      className="text-teal-600 font-medium hover:underline"
                    >
                      hola@sendhapy.com
                    </a>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CasilleroSection;
